// frontend/src/store/adminStore.js

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import adminAPI from '../services/admin.api.js';
import { toast } from 'react-hot-toast';

const useAdminStore = create(
  persist(
    (set, get) => ({
      // State
      users: [],
      selectedUser: null,
      transactions: [],
      selectedTransaction: null,
      flaggedContent: [],
      dashboardStats: null,
      adminActions: [],
      vtuProducts: [],
      dataProviderConfig: [],
      airtimeProviderConfig: null,
      
      // Wallet Transactions State
      walletTransactions: [],
      walletTransactionsPagination: null,
      
      // Marketplace State
      marketplaceBusinesses: [],
      selectedBusiness: null,
      marketplaceReviews: [],
      selectedReview: null,
      marketplaceOrders: [],
      selectedOrder: null,
      
      // Announcement & Promotion State
      announcements: [],
      promotions: [],
      announcementsPagination: null,
      promotionsPagination: null,
      selectedAnnouncement: null,
      selectedPromotion: null,
      showAnnouncementModal: false,
      showPromotionModal: false,
      
      // Pagination
      usersPagination: null,
      transactionsPagination: null,
      flaggedContentPagination: null,
      adminActionsPagination: null,
      vtuProductsPagination: null,
      marketplaceBusinessesPagination: null,
      marketplaceReviewsPagination: null,
      marketplaceOrdersPagination: null,
      
      // UI State
      isLoading: false,
      error: null,
      activeTab: 'dashboard',
      showUserModal: false,
      showTransactionModal: false,
      showReportModal: false,
      showVtuProductModal: false,
      showBusinessModal: false,
      showReviewModal: false,
      showOrderModal: false,
      showSettingsModal: false,
      
      // =================== USER MANAGEMENT ===================
      getUsers: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.getUsers(params);
          
          set({
            users: response.data.users,
            usersPagination: response.data.pagination,
          });
          
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getUserDetails: async (userId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.getUserDetails(userId);
          
          set({ selectedUser: response.data });
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateUser: async (userId, userData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.updateUser(userId, userData);
          
          // Update in users list
          set(state => ({
            users: state.users.map(user => 
              user.id === userId ? response.data : user
            ),
            selectedUser: response.data,
          }));
          
          toast.success('User updated successfully');
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateUserRole: async (userId, newRole) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.updateUserRole(userId, newRole);
          
          toast.success('User role updated successfully!');
          
          // Update in users list
          set(state => ({
            users: state.users.map(user => 
              user.id === userId ? { ...user, role: newRole } : user
            ),
            selectedUser: state.selectedUser?.id === userId 
              ? { ...state.selectedUser, role: newRole } 
              : state.selectedUser,
          }));
          
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      banUser: async (userId, reason) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.banUser(userId, reason);
          
          toast.success('User banned successfully!');
          
          // Update user in list
          set(state => ({
            users: state.users.filter(user => user.id !== userId),
            selectedUser: null,
          }));
          
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      unbanUser: async (userId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.unbanUser(userId);
          
          toast.success('User unbanned successfully!');
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      suspendUser: async (userId, reason, days = 7) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.suspendUser(userId, reason, days);
          
          // Remove from list or update status
          set(state => ({
            users: state.users.filter(user => user.id !== userId),
          }));
          
          toast.success(`User suspended for ${days} days`);
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      unsuspendUser: async (userId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.unsuspendUser(userId);
          
          toast.success('User unsuspended successfully');
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== TRANSACTION MANAGEMENT ===================
      getTransactions: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.getTransactions(params);
          
          set({
            transactions: response.data.transactions,
            transactionsPagination: response.data.pagination,
          });
          
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getTransactionDetails: async (transactionId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.getTransactionDetails(transactionId);
          
          set({ selectedTransaction: response.data });
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      retryTransaction: async (transactionId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.retryTransaction(transactionId);
          
          toast.success('Transaction retry initiated!');
          
          // Update transaction status
          set(state => ({
            transactions: state.transactions.map(transaction =>
              transaction.id === transactionId
                ? { ...transaction, status: 'PENDING' }
                : transaction
            ),
          }));
          
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      refundTransaction: async (transactionId, reason) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.refundTransaction(transactionId, reason);
          
          toast.success('Transaction refunded successfully!');
          
          // Update transaction
          set(state => ({
            transactions: state.transactions.map(transaction =>
              transaction.id === transactionId
                ? { 
                    ...transaction, 
                    status: 'REVERSED',
                    metadata: {
                      ...transaction.metadata,
                      refunded: true,
                      refundReason: reason,
                    }
                  }
                : transaction
            ),
          }));
          
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== WALLET TRANSACTIONS ===================
      getWalletTransactions: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.getWalletTransactions(params);
          
          set({
            walletTransactions: response.data.transactions,
            walletTransactionsPagination: response.data.pagination,
          });
          
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      clearWalletTransactions: () => set({ 
        walletTransactions: [], 
        walletTransactionsPagination: null 
      }),

      // =================== CONTENT MODERATION ===================
      getFlaggedContent: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.getFlaggedContent(params);
          
          // Map reports to match the expected format in the UI
          const mappedReports = response.data.reports.map(report => ({
            id: report.id,
            reason: report.reason,
            description: report.description,
            status: report.status,
            createdAt: report.createdAt,
            reporter: report.reporter,
            post: report.post,
            comment: report.comment,
            reportedUser: report.reportedUser,
            // Add these for compatibility with existing UI
            postId: report.postId,
            commentId: report.commentId,
            userId: report.userId
          }));
          
          set({
            flaggedContent: mappedReports,
            flaggedContentPagination: response.data.pagination,
          });
          
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      resolveReport: async (reportId, action, resolution) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.resolveReport(reportId, action, resolution);
          
          toast.success('Report resolved successfully!');
          
          // Remove from flagged content
          set(state => ({
            flaggedContent: state.flaggedContent.filter(report => report.id !== reportId),
          }));
          
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      removeContent: async (contentType, contentId, reason) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.removeContent(contentType, contentId, reason);
          
          toast.success('Content removed successfully!');
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== ANALYTICS ===================
      getDashboardStats: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.getDashboardStats(params);
          
          set({ dashboardStats: response.data });
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== ADMIN ACTIONS LOG ===================
      getAdminActions: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.getAdminActions(params);
          
          set({
            adminActions: response.data.actions,
            adminActionsPagination: response.data.pagination,
          });
          
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== VTU PRODUCT MANAGEMENT ===================
      getVtuProducts: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.getVtuProducts(params);
          
          set({
            vtuProducts: response.data.products,
            vtuProductsPagination: response.data.pagination,
          });
          
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateVtuProductCommission: async (productId, commission) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.updateVtuProductCommission(productId, commission);
          
          toast.success('Commission updated successfully!');
          
          // Update in products list
          set(state => ({
            vtuProducts: state.vtuProducts.map(product =>
              product.id === productId ? response.data : product
            ),
          }));
          
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      syncVtuProducts: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.syncVtuProducts();
          
          toast.success('Product sync completed!');
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== DATA PROVIDER CONFIG ===================
      getDataProviderConfig: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.getDataProviderConfig();
          set({ dataProviderConfig: response.data });
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateDataProviderConfig: async (network, providerType) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.updateDataProviderConfig(network, providerType);

          toast.success(
            `${network} data provider updated to ${providerType}`
          );

          // Update local state for this network
          set(state => ({
            dataProviderConfig: state.dataProviderConfig.map(row =>
              row.network === network
                ? { ...row, ...response.data }
                : row
            ),
          }));

          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== AIRTIME PROVIDER CONFIG ===================
      getAirtimeProviderConfig: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.getAirtimeProviderConfig();
          set({ airtimeProviderConfig: response.data });
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateAirtimeProviderConfig: async (providerType) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.updateAirtimeProviderConfig(providerType);
          toast.success(`Airtime provider updated to ${providerType}`);
          set({ airtimeProviderConfig: response.data });
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== MARKETPLACE BUSINESS MANAGEMENT ===================
      getAllBusinesses: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.getAllBusinesses(params);
          
          set({
            marketplaceBusinesses: response.data.businesses,
            marketplaceBusinessesPagination: response.data.pagination,
          });
          
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getBusinessDetailsAdmin: async (businessId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.getBusinessDetailsAdmin(businessId);
          
          set({ selectedBusiness: response.data });
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateBusinessAdmin: async (businessId, data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.updateBusinessAdmin(businessId, data);
          
          toast.success('Business updated successfully!');
          
          // Update in list
          set(state => ({
            marketplaceBusinesses: state.marketplaceBusinesses.map(business =>
              business.id === businessId ? response.data : business
            ),
            selectedBusiness: state.selectedBusiness?.id === businessId 
              ? response.data 
              : state.selectedBusiness,
          }));
          
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      deleteBusinessAdmin: async (businessId) => {
        set({ isLoading: true, error: null });
        try {
          await adminAPI.deleteBusinessAdmin(businessId);
          
          toast.success('Business deleted successfully!');
          
          set(state => ({
            marketplaceBusinesses: state.marketplaceBusinesses.filter(b => b.id !== businessId),
            selectedBusiness: null,
          }));
          
          return true;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== BUSINESS VERIFICATION ===================
      verifyBusiness: async (businessId, verified) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.verifyBusiness(businessId, verified);
          
          toast.success(verified ? 'Business verified successfully!' : 'Business verification removed');
          
          // Update the business in the current list
          set(state => ({
            marketplaceBusinesses: state.marketplaceBusinesses.map(business => 
              business.id === businessId ? { ...business, verified: verified } : business
            ),
            selectedBusiness: state.selectedBusiness?.id === businessId 
              ? { ...state.selectedBusiness, verified: verified } 
              : state.selectedBusiness,
          }));
          
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== MARKETPLACE REVIEWS ===================
      getAllReviews: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.getAllReviews(params);
          
          set({
            marketplaceReviews: response.data.reviews,
            marketplaceReviewsPagination: response.data.pagination,
          });
          
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      moderateReview: async (reviewId, status, moderationNotes = null) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.moderateReview(reviewId, status, moderationNotes);
          
          toast.success(`Review ${status.toLowerCase()}!`);
          
          set(state => ({
            marketplaceReviews: state.marketplaceReviews.filter(r => r.id !== reviewId),
          }));
          
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== MARKETPLACE ORDERS ===================
      getAllOrders: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.getAllOrders(params);
          
          set({
            marketplaceOrders: response.data.orders,
            marketplaceOrdersPagination: response.data.pagination,
          });
          
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getOrderDetailsAdmin: async (orderId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.getOrderDetailsAdmin(orderId);
          
          set({ selectedOrder: response.data });
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateOrderAdmin: async (orderId, data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.updateOrderAdmin(orderId, data);
          
          toast.success('Order updated successfully!');
          
          set(state => ({
            marketplaceOrders: state.marketplaceOrders.map(order =>
              order.id === orderId ? response.data : order
            ),
            selectedOrder: state.selectedOrder?.id === orderId 
              ? response.data 
              : state.selectedOrder,
          }));
          
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== ANNOUNCEMENT MANAGEMENT ===================
      getAllAnnouncements: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.getAllAnnouncements(params);
          set({
            announcements: response.data.announcements,
            announcementsPagination: response.data.pagination,
            isLoading: false,
          });
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      createAnnouncement: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.createAnnouncement(data);
          toast.success('Announcement created successfully!');
          set({ isLoading: false });
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      updateAnnouncement: async (id, data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.updateAnnouncement(id, data);
          toast.success('Announcement updated successfully!');
          set({ isLoading: false });
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      deleteAnnouncement: async (id) => {
        set({ isLoading: true, error: null });
        try {
          await adminAPI.deleteAnnouncement(id);
          toast.success('Announcement deleted successfully!');
          set(state => ({
            announcements: state.announcements.filter(a => a.id !== id),
            isLoading: false,
          }));
          return true;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      // =================== PROMOTION MANAGEMENT ===================
      getAllPromotions: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.getAllPromotions(params);
          set({
            promotions: response.data.promotions,
            promotionsPagination: response.data.pagination,
            isLoading: false,
          });
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      createPromotion: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.createPromotion(data);
          toast.success('Promotion created successfully!');
          set({ isLoading: false });
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      updatePromotion: async (id, data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAPI.updatePromotion(id, data);
          toast.success('Promotion updated successfully!');
          set({ isLoading: false });
          return response.data;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      deletePromotion: async (id) => {
        set({ isLoading: true, error: null });
        try {
          await adminAPI.deletePromotion(id);
          toast.success('Promotion deleted successfully!');
          set(state => ({
            promotions: state.promotions.filter(p => p.id !== id),
            isLoading: false,
          }));
          return true;
        } catch (error) {
          const message = error.response?.data?.error?.message || error.message;
          toast.error(message);
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      // =================== UI ACTIONS ===================
      setActiveTab: (tab) => set({ activeTab: tab }),
      
      setSelectedUser: (user) => set({ selectedUser: user }),
      setSelectedTransaction: (transaction) => set({ selectedTransaction: transaction }),
      setSelectedBusiness: (business) => set({ selectedBusiness: business }),
      setSelectedReview: (review) => set({ selectedReview: review }),
      setSelectedOrder: (order) => set({ selectedOrder: order }),
      setSelectedAnnouncement: (announcement) => set({ selectedAnnouncement: announcement }),
      setSelectedPromotion: (promotion) => set({ selectedPromotion: promotion }),
      
      setShowUserModal: (show) => set({ showUserModal: show }),
      setShowTransactionModal: (show) => set({ showTransactionModal: show }),
      setShowReportModal: (show) => set({ showReportModal: show }),
      setShowSettingsModal: (show) => set({ showSettingsModal: show }),
      setShowVtuProductModal: (show) => set({ showVtuProductModal: show }),
      setShowBusinessModal: (show) => set({ showBusinessModal: show }),
      setShowReviewModal: (show) => set({ showReviewModal: show }),
      setShowOrderModal: (show) => set({ showOrderModal: show }),
      setShowAnnouncementModal: (show) => set({ showAnnouncementModal: show }),
      setShowPromotionModal: (show) => set({ showPromotionModal: show }),
      
      clearSelectedUser: () => set({ selectedUser: null }),
      clearSelectedTransaction: () => set({ selectedTransaction: null }),
      clearSelectedBusiness: () => set({ selectedBusiness: null }),
      clearSelectedReview: () => set({ selectedReview: null }),
      clearSelectedOrder: () => set({ selectedOrder: null }),
      clearSelectedAnnouncement: () => set({ selectedAnnouncement: null }),
      clearSelectedPromotion: () => set({ selectedPromotion: null }),
      
      clearError: () => set({ error: null }),
    }),
    {
      name: 'admin-storage',
      partialize: (state) => ({
        systemSettings: state.systemSettings,
      }),
    }
  )
);

export default useAdminStore;